import clsx from "clsx";

import { AppShell } from "@chef/feature-app-shell";
import { Trash } from "@chef/icons/small";
import {
  useAppDispatch,
  useExtendedBasketData,
} from "@chef/state-management/hooks";
import { loadCalendar, resetDeviations } from "@chef/state-management";
import { CurrencyWrapper as Currency } from "@chef/smart-components";
import { getBasketDifference } from "@chef/state-management/helpers";
import { useIntercom } from "@chef/hooks";
import { sumByFn } from "@chef/utils/array";

import type { WeekYear } from "@chef/types/common";

import { CartStep } from "./CartStep";
import { Summary } from "./Summary";
import { CartStepProgressButton } from "./CartStepProgressButton";
import { useSubmitOrder } from "./useSubmitOrder";

import { intl } from "./WeekEditorConfirmOrderExperimental.Intl";

export type Step = "cart" | "upsell";

export const WeekEditorConfirmOrderExperimental = ({
  week,
  year,
}: WeekYear) => {
  const dispatch = useAppDispatch();
  const { handleSubmitOrder, isSubmitOrderLoading } = useSubmitOrder({
    week,
    year,
  });
  const { data: extendedBasket } = useExtendedBasketData({
    week,
    year,
  });

  useIntercom(false);

  const { basketHasDeviations, selectedProducts, chosenProducts } =
    extendedBasket;

  const isOnCartStep = true;
  const headerText = isOnCartStep ? intl.ORDER : intl.LAST_CHANCE;

  const handleUndoChanges = () => {
    dispatch(resetDeviations());
    dispatch(loadCalendar({ week, year, range: 1 }));
  };

  const basketDifference = getBasketDifference(
    chosenProducts,
    selectedProducts ?? [],
  );
  const basketCostDifference = sumByFn(
    basketDifference,
    (product) => product.price * product.quantity,
  );

  const showCartProgressButton =
    !isOnCartStep || (isOnCartStep && basketHasDeviations);

  return (
    <AppShell title={headerText}>
      <AppShell.Meta />
      <AppShell.Header variant="my-menu" />

      <AppShell.Main>
        <h1 className={clsx("block mb-6", isOnCartStep && "hidden md:block")}>
          <strong>{headerText}</strong>
        </h1>

        <div className="relative flex flex-col md:gap-8 md:flex-row">
          <section className="flex flex-col w-full md:w-3/5 lg:w-2/3">
            <CartStep week={week} year={year} />
          </section>

          <section
            className={clsx(
              "relative flex flex-col gap-4 md:w-2/5 lg:w-1/3",
              basketHasDeviations && isOnCartStep && "mb-20",
            )}
          >
            <div
              className={clsx(
                "flex flex-col gap-4 md:gap-0 md:top-60 md:bg-white md:shadow-md md:sticky",
                !isOnCartStep && "mb-10",
              )}
            >
              <Summary week={week} year={year} />
              {showCartProgressButton && (
                <div className="fixed z-20 w-full px-4 pb-4 -translate-x-1/2 md:relative md:translate-x-0 bottom-18 md:bottom-0 left-1/2 md:left-0">
                  <CartStepProgressButton
                    isLoading={isSubmitOrderLoading}
                    onClick={handleSubmitOrder}
                  >
                    {isOnCartStep ? (
                      <div className="flex justify-between">
                        <span>{intl.CONFIRM_CHANGES}</span>

                        {basketHasDeviations && (
                          <Currency
                            trimDecimal
                            prefix={basketCostDifference > 0 ? "+ " : "- "}
                          >
                            {basketCostDifference}
                          </Currency>
                        )}
                      </div>
                    ) : (
                      intl.CONFIRM_ORDER
                    )}
                  </CartStepProgressButton>
                </div>
              )}

              {isOnCartStep && basketHasDeviations && (
                <div className="relative">
                  <button
                    className="absolute w-full space-x-2 -translate-x-1/2 md:top-4 left-1/2"
                    type="button"
                    onClick={handleUndoChanges}
                  >
                    <Trash className="w-5 h-5" />
                    <span className="underline underline-offset-8 decoration-2">
                      {intl.UNDO_CHANGES}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      </AppShell.Main>
      <AppShell.TabBar variant="my-menu" />
    </AppShell>
  );
};
